import React, { useEffect, useMemo, useState } from 'react';
import { Check, Plus, Pencil, Trash2 } from 'lucide-react';
import { Button } from '../../../../../../../../components/ui/button';
import { Card, CardContent } from '../../../../../../../../components/ui/card';
import { cn } from '../../../../../../../../lib/utils';
import { formatLabel, StatusBadge } from '../../pipeMillReportUtils';
import {
  AdjudicationPanel,
  BendDispositionPanel,
  ChemicalFormulaRow,
  ChemistryResultRow,
  FlatteningDispositionPanel,
  HydrostaticDispositionPanel,
  ItemActionButtons,
  KeyValueRows,
  MiniStatusDot,
  PipeLocationDiagram,
  ReportMetadataRail,
  ResultRow,
  SectionHead,
  StatCard,
  TensileResultRow,
} from './shared/components';
import {
  AUT_DETAIL,
  BEND_DETAIL,
  CHEMICAL_DETAIL,
  CVN_DETAIL,
  DIMENSIONAL_DETAIL,
  HARDNESS_DETAIL,
  HYDROSTATIC_DETAIL,
  STANDARD_REPORT_DETAILS,
} from './shared/sampleData';
import {
  average,
  formatDisplayDate,
  getStandardReportSummaryStats,
  standardLimitText,
  standardScaleMax,
  standardScaleMin,
  statusNoteClassName,
} from './shared/format';
import {
  createSampleMeasurement,
  isSampleDataDeleted,
} from './shared/measurements';
import {
  getProjectLimitField,
  getProjectLimitMax,
  getProjectLimitMin,
  getProjectLimitRows,
  getProjectLimitText,
} from './shared/limits';
import type {
  ChemicalElementDisplay,
  FullReportType,
  ProjectLimitFieldOption,
  ReportDefinition,
  ReportMeasurementItem,
  ReportMetadataEntry,
  ReportSummary,
  StandardCheck,
} from './shared/types';
import type { BaseReportDetailProps } from './shared/reportProps';

export const MflEtReportDetail: React.FC<{
  heatNumber: string;
  metadata?: ReportMetadataEntry;
  projectLimitFields?: ProjectLimitFieldOption[];
  frequency?: string;
  measurements?: ReportMeasurementItem[];
  deletedMeasurementIds?: string[];
  onAddMeasurement?: (reportType: FullReportType) => void;
  onEditMeasurement?: (measurement: ReportMeasurementItem) => void;
  onDeleteMeasurement?: (measurement: ReportMeasurementItem) => void;
}> = ({ heatNumber, metadata, projectLimitFields = [], frequency, measurements = [], onAddMeasurement, onEditMeasurement, onDeleteMeasurement }) => {
  const mflMeasurements = measurements.filter((measurement) => measurement.reportType === 'MFL_ET');
  const [activeMflId, setActiveMflId] = useState<string | null>(null);
  const effectiveFrequency = metadata?.frequency || frequency || 'per ERW/HFW pipe';
  const signalMax = getProjectLimitMax(projectLimitFields, 'mfl_signal_pct_ref', 100);
  const calNotchMin = getProjectLimitMin(projectLimitFields, 'mfl_cal_notch_pct_wt', 12.5);
  const calNotchMax = getProjectLimitMax(projectLimitFields, 'mfl_cal_notch_pct_wt', 12.5);
  const signalLimitText = getProjectLimitText(projectLimitFields, 'mfl_signal_pct_ref', `<= ${signalMax} % ref`);
  const calNotchLimitText = getProjectLimitText(projectLimitFields, 'mfl_cal_notch_pct_wt', `${calNotchMin === calNotchMax ? calNotchMin : `${calNotchMin} - ${calNotchMax}`} % WT`);
  const projectLimitRows = getProjectLimitRows(projectLimitFields, [
    ['Signal max', signalLimitText, true],
    ['Cal notch', calNotchLimitText, true],
  ]);
  const getMflStatus = (signalPctRef: number, calNotchPctWt: number, disposition: string) => {
    if (disposition === 'reject') return 'FAIL';
    if (disposition === 'confirmed_by_AUT' || disposition === 'repaired') return 'PENDING_REVIEW';
    if (signalPctRef > signalMax) return 'FAIL';
    if (calNotchPctWt < calNotchMin || calNotchPctWt > calNotchMax) return 'FAIL';
    return 'PASS';
  };
  const indications = mflMeasurements.map((measurement) => {
    const signalPctRef = Number(measurement.mflSignalPctRef) || 0;
    const calNotchPctWt = Number(measurement.mflCalNotchPctWt) || 0;
    const disposition = measurement.mflDisposition || 'accept';
    return {
      id: measurement.mflIndicationId || measurement.id,
      flawType: measurement.mflFlawType || 'hook_crack',
      signalPctRef,
      calNotchPctWt,
      disposition,
      status: getMflStatus(signalPctRef, calNotchPctWt, disposition),
      measurement,
    };
  });
  useEffect(() => {
    if (!indications.length) {
      setActiveMflId(null);
      return;
    }

    if (!activeMflId || !indications.some((indication) => indication.id === activeMflId)) {
      setActiveMflId(indications[0].id);
    }
  }, [activeMflId, indications]);
  const activeIndication = indications.find((indication) => indication.id === activeMflId) || indications[0];
  const reviewCount = indications.filter((indication) => indication.status === 'PENDING_REVIEW').length;
  const failCount = indications.filter((indication) => indication.status === 'FAIL').length;
  const route = activeIndication?.measurement?.mflRoute || mflMeasurements[0]?.mflRoute || '-';

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_340px]">
      <main className="space-y-5 p-6">
        <Card className="overflow-hidden border-[#E2E5EA]">
          <CardContent className="p-0">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[#E2E5EA] bg-white px-4 py-3">
              <div>
                <h3 className="font-heading text-[15px] font-semibold text-blueprint-blue">MFL / ET indications</h3>
                <div className="mt-1 text-[11px] text-heritage-grey">{activeIndication ? `Selected ${activeIndication.id} · ${route}` : 'No MFL / ET indications saved yet'}</div>
              </div>
              <Button type="button" size="sm" onClick={() => onAddMeasurement?.('MFL_ET')} className="bg-hero-red text-white hover:bg-hero-red-60">
                <Plus className="mr-2 h-4 w-4" />
                Add Indication
              </Button>
            </div>
            <table className="w-full text-left">
              <thead className="border-b border-[#E2E5EA] bg-page-bg text-[10px] font-bold uppercase tracking-wider text-heritage-grey">
                <tr>
                  <th className="px-4 py-3">ID</th>
                  <th className="px-4 py-3">Flaw type</th>
                  <th className="px-4 py-3 text-right">Signal % ref</th>
                  <th className="px-4 py-3 text-right">Cal notch % WT</th>
                  <th className="px-4 py-3">Disposition</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#EEF0F3] text-[12px] text-text-grey">
                {indications.map((indication) => (
                  <tr key={indication.id} onClick={() => indication.measurement && setActiveMflId(indication.id)} className={cn(indication.measurement && 'cursor-pointer hover:bg-page-bg', activeMflId === indication.id && 'bg-[#E7F3FF]')}>
                    <td className="px-4 py-3 font-mono font-semibold">{indication.id}</td>
                    <td className="px-4 py-3">{indication.flawType.replace('_', ' ')}</td>
                    <td className={cn('px-4 py-3 text-right font-mono', indication.signalPctRef > signalMax && 'text-readable-red')}>{indication.signalPctRef}</td>
                    <td className={cn('px-4 py-3 text-right font-mono', (indication.calNotchPctWt < calNotchMin || indication.calNotchPctWt > calNotchMax) && 'text-readable-red')}>{indication.calNotchPctWt}</td>
                    <td className="px-4 py-3">{indication.disposition.replace('_', ' ')}</td>
                    <td className="px-4 py-3"><StatusBadge status={indication.status} /></td>
                    <td className="px-4 py-3 text-right">
                      {indication.measurement && (
                        <ItemActionButtons measurement={indication.measurement} onEditMeasurement={onEditMeasurement} onDeleteMeasurement={onDeleteMeasurement} />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>

        <div>
          <SectionHead title="Process rules" />
          <Card className="border-[#E2E5EA]">
            <CardContent className="p-4">
              <ul className="list-disc space-y-2 pl-5 text-[12px] leading-5 text-text-grey">
                <li>Signal over {signalLimitText} is a rejection unless disposition is <span className="font-mono">confirmed_by_AUT</span> or <span className="font-mono">repaired</span>.</li>
                <li>Calibration notch must meet {calNotchLimitText}.</li>
                <li>MFL/ET rejection is provisional - the pipe is not formally failed until AUT or RT confirms.</li>
                <li>Every <span className="font-mono">confirmed_by_AUT</span> disposition requires a follow-up AUT record on the same pipe.</li>
              </ul>
            </CardContent>
          </Card>
        </div>
      </main>

      <ReportMetadataRail
        rows={[
          ['Report ID', metadata?.reportId || '-', true],
          ['Performed at', formatDisplayDate(metadata?.performedAt), true],
          ['Heat', heatNumber, true],
          ['Lab', metadata?.lab || '-'],
          ['Technician', metadata?.technician || '-'],
          ['Source', metadata?.source ? formatLabel(metadata.source) : '-'],
          ['Source file', metadata?.sourceFile || '-', true],
        ]}
        extraTitle="Limits In Effect"
        extraRows={projectLimitRows}
      >
        <div className="mt-5 text-[10px] font-bold uppercase tracking-wider text-heritage-grey">Frequency / Scope</div>
        <Card className="mt-3 border-[#E2E5EA]">
          <CardContent className="p-4">
            <KeyValueRows rows={[['Frequency', effectiveFrequency, true], ['Indications', `${mflMeasurements.length} saved`, true], ['Pending review', reviewCount, true], ['Rejects', failCount, true]]} />
          </CardContent>
        </Card>
        <AdjudicationPanel />
      </ReportMetadataRail>
    </div>
  );
};
